import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayContainer,
  OverlayRef,
} from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { ComponentRef, Inject, Injectable, OnDestroy, ViewContainerRef } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EMOJI_GROUPED } from './emoji-datasource';
import { EMOJI_ROW_SIZE, EmojiListComponent } from './emoji-list/emoji-list.component';
import { cachedStringSearch } from './helpers';
import {
  EmojiVirtualScrollData,
  EmojiListNavigationKeys,
  EmojiItem,
  FlatArrayItemType,
} from './types';

interface OverlayOrigin {
  top: number;
  left: number;
  right?: number;
  bottom?: number;
}

interface EmojiPickerOpenOptions {
  withSearch?: boolean;
  initialSearchValue?: string;
  preferredPositions?: ConnectedPosition[];
}

@Injectable()
export class EmojiPickerService implements OnDestroy {
  public filterEmojis = cachedStringSearch(
    (query: string): EmojiVirtualScrollData =>
      EMOJI_GROUPED.reduce((acc, group) => {
        const list = group.list.filter(({ shortName }) => shortName.includes(query));

        if (!list.length) return acc;

        let chunks: EmojiVirtualScrollData = [];
        let index = 0;
        while (index < list.length) {
          chunks = [
            ...chunks,
            { type: FlatArrayItemType.EmojiRow, data: list.slice(index, index + EMOJI_ROW_SIZE) },
          ];
          index += EMOJI_ROW_SIZE;
        }

        return acc.concat({ type: FlatArrayItemType.Heading, name: group.name }, chunks);
      }, <EmojiVirtualScrollData>[]),
  );

  private componentRef: ComponentRef<EmojiListComponent> | null = null;
  private overlayRef: OverlayRef | null = null;
  private positionStrategy: FlexibleConnectedPositionStrategy | null = null;
  private ngOnDestroy$ = new Subject();
  private positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  private portalRef: ComponentPortal<EmojiListComponent> | null;

  constructor(
    private overlay: Overlay,
    private vcRef: ViewContainerRef,
    private _viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) private _document: Document,
    private _platform: Platform,
    private _overlayContainer: OverlayContainer,
  ) {}

  public ngOnDestroy(): void {
    this.close();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public open(
    origin: OverlayOrigin,
    _options: EmojiPickerOpenOptions = {},
  ): {
    select$: Observable<EmojiItem>;
    element: HTMLElement;
    backdropClick$: Observable<MouseEvent>;
  } {
    const options: Required<EmojiPickerOpenOptions> = {
      withSearch: _options.withSearch || false,
      initialSearchValue: _options.initialSearchValue || '',
      preferredPositions: _options.preferredPositions || [],
    };
    this.close();
    this.openComponent(EmojiListComponent, origin, options.preferredPositions);
    const { instance, location, changeDetectorRef } = this.componentRef!;

    instance.queryChange = (query: string) => {
      const data = this.filterEmojis(query);
      this.setData(data);
    };
    instance.searchEnabled = options.withSearch;
    if (options.initialSearchValue) {
      instance.searchFormControl.setValue(options.initialSearchValue);
    }

    changeDetectorRef.detectChanges();
    this.overlayRef!.updatePosition();

    return {
      select$: instance.select$,
      element: location.nativeElement,
      backdropClick$: this.overlayRef!.backdropClick(),
    };
  }

  public setData(data: EmojiVirtualScrollData): void {
    if (!this.componentRef) return;

    this.componentRef.instance.filteredEmojis = data;
    this.componentRef.changeDetectorRef.detectChanges();
  }

  public handleKey(key: EmojiListNavigationKeys): void {
    if (!this.componentRef) return;

    this.componentRef.instance.handleKey(key);
  }

  public close(): void {
    if (this.componentRef) {
      this.componentRef.instance.select$.complete();
      this.componentRef.destroy();
    }
    this.componentRef = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.positionStrategy?.dispose();
    this.positionStrategy = null;
  }

  public updatePosition(position: OverlayOrigin): void {
    if (!this.overlayRef || !this.positionStrategy) return;

    this.positionStrategy.setOrigin({
      x: position.left,
      y: position.top,
      width: (position.right || position.left) - position.left,
      height: (position.bottom || position.top) - position.top,
    });
    this.overlayRef.updatePosition();
  }

  public isOpen(): boolean {
    return !!this.componentRef;
  }

  private openComponent(
    component: new (...args: any[]) => EmojiListComponent,
    origin: OverlayOrigin,
    preferredPositions: ConnectedPosition[],
  ) {
    this.positionStrategy = new FlexibleConnectedPositionStrategy(
      {
        x: origin.left,
        y: origin.top,
        width: (origin.right || origin.left) - origin.left,
        height: (origin.bottom || origin.top) - origin.top,
      },
      this._viewportRuler,
      this._document,
      this._platform,
      this._overlayContainer,
    );
    this.positionStrategy.withGrowAfterOpen(true);
    this.positionStrategy.withPush(false);
    this.overlayRef = this.overlay.create({
      positionStrategy: this.positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.close());
    this.positionStrategy.withPositions([...preferredPositions, ...this.positions]);
    this.portalRef = new ComponentPortal(component, this.vcRef);
    this.componentRef = this.overlayRef.attach(this.portalRef);
  }
}
